import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Spinner } from './components/ui/misc'
import { useMe } from './lib/api'
import { AppShell } from './components/layout/AppShell'
import { DocsRoute } from './components/DocsLink'
import { ApiKeysPage } from './pages/ApiKeys'
import { AcceptInvitePage } from './pages/auth/AcceptInvite'
import { LoginPage } from './pages/auth/Login'
import { ForgotPasswordPage, ResetPasswordPage } from './pages/auth/Password'
import { SignupPage } from './pages/auth/Signup'
import { HomePage } from './pages/Home'
import { PlaygroundPage } from './pages/Playground'
import { BillingPage } from './pages/settings/Billing'
import { MembersPage } from './pages/settings/Members'
import { OrganizationPage } from './pages/settings/Organization'
import { ProfilePage } from './pages/settings/Profile'
import { SettingsPage } from './pages/settings/SettingsLayout'

// keep the charts on Usage out of the main bundle
const page = <T extends string>(load: () => Promise<Record<T, React.ComponentType>>, name: T) =>
  lazy(() => load().then((m) => ({ default: m[name] })))
const UsagePage = page(() => import('./pages/Usage'), 'UsagePage')

export default function App() {
  return (
    <Suspense fallback={<Spinner />}>
    <Routes>
      <Route path="login" element={<LoginPage />} />
      <Route path="signup" element={<SignupPage />} />
      {/* the waitlist form while the beta is invite-only (the same page as /signup) */}
      <Route path="waitlist" element={<SignupPage />} />
      <Route path="request-access" element={<Navigate to="/waitlist" replace />} />
      <Route path="forgot-password" element={<ForgotPasswordPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="invite/:id" element={<AcceptInvitePage />} />

      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/home" replace />} />
        <Route path="home" element={<HomePage />} />
        <Route path="playground" element={<PlaygroundPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="docs" element={<DocsRoute />} />
        <Route path="settings">
          <Route index element={<Navigate to="profile" replace />} />
          <Route
            path="profile"
            element={
              <SettingsPage title="Personal settings">
                <ProfilePage />
              </SettingsPage>
            }
          />
          <Route
            path="organization"
            element={
              <SettingsPage title="Organization settings">
                <OrganizationPage />
              </SettingsPage>
            }
          />
          <Route
            path="members"
            element={
              <SettingsPage title="Members" wide>
                <MembersPage />
              </SettingsPage>
            }
          />
          {/* budgets are a section of Billing now */}
          <Route path="budgets" element={<Navigate to="/settings/billing" replace />} />
          <Route
            path="billing"
            element={
              <SettingsPage title="Billing">
                <BillingPage />
              </SettingsPage>
            }
          />
        </Route>
      </Route>

      {/* the admin app has its own host now */}
      <Route path="admin/*" element={<AdminMoved />} />

      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
    </Suspense>
  )
}

/** Old /admin links: the admin app lives on its own host (only admins are told where). */
function AdminMoved() {
  const { data: me, isLoading } = useMe()
  if (isLoading) return <Spinner />
  if (me?.adminUrl) {
    window.location.replace(me.adminUrl)
    return <Spinner />
  }
  return <Navigate to="/home" replace />
}
